import { useEffect, useMemo, useState } from "react";
import { getAICommandCenterData } from "../../../api/aiCommandCenterService";

export default function AICommandCenterPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            setLoading(true);
            setError("");

            const result = await getAICommandCenterData();
            setData(result);
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to load AI Command Center."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const cards = useMemo(() => {
        if (!data) return [];

        return [
            ["AI Risk", `${data.aiRisk}%`, "red"],
            ["AI Confidence", `${data.aiConfidence}%`, "green"],
            ["Route Optimization", `${data.routeOptimization}%`, "blue"],
            ["Fraud Risk", `${data.fraudRisk}%`, "orange"],
            ["ETA Accuracy", `${data.etaAccuracy}%`, "teal"],
            ["Trust Prediction", `${data.trustPrediction}%`, "purple"],
            ["Security Threat", `${data.securityThreat}%`, "red"],
            ["Anomaly Events", data.anomalyEvents, "slate"],
        ];
    }, [data]);

    if (loading)
        return (
            <div className="p-8 text-slate-600">
                Loading AI Command Center...
            </div>
        );

    if (error)
        return (
            <div className="rounded-xl border border-red-300 bg-red-50 p-5 text-red-700">
                {error}
            </div>
        );

    return (
        <div className="space-y-6">

            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-5">

                <div>
                    <h1 className="text-4xl font-black">
                        AI Command Center
                    </h1>

                    <p className="text-slate-500 mt-2">
                        Unified artificial intelligence platform for routing,
                        ETA prediction, trust evaluation, anomaly detection,
                        blockchain analysis and logistics optimization.
                    </p>
                </div>

                <button
                    onClick={load}
                    className="bg-slate-900 hover:bg-black text-white rounded-xl px-6 py-3 font-semibold"
                >
                    Refresh AI
                </button>

            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">

                {cards.map(([title, value, color]) => (
                    <KpiCard
                        key={title}
                        title={title}
                        value={value}
                        color={color}
                    />
                ))}

            </div>

            <div className="grid lg:grid-cols-3 gap-6">

                <Panel
                    title="Operational Context"
                    subtitle="Current AI workload"
                >

                    <Metric
                        label="Shipments Analysed"
                        value={data.shipments}
                    />

                    <Metric
                        label="IoT Devices"
                        value={data.devices}
                    />

                    <Metric
                        label="Blockchain Events"
                        value={data.blockchainEvents}
                    />

                </Panel>

                <Panel
                    title="AI Platform Health"
                    subtitle="Subsystem status"
                >

                    <Status text="Inference Engine" />

                    <Status text="Trust Engine" />

                    <Status text="Digital Twin AI" />

                    <Status text="ETA Prediction" />

                    <Status text="Blockchain Analytics" />

                </Panel>

                <Panel
                    title="AI Recommendations"
                    subtitle="Generated by AI engine"
                >

                    {data.recommendations?.length > 0 ? (
                        data.recommendations.map((r, i) => (
                            <Recommendation
                                key={i}
                                text={r}
                            />
                        ))
                    ) : (
                        <div className="text-slate-500">
                            No recommendations.
                        </div>
                    )}

                </Panel>

            </div>

        </div>
    );
}

function KpiCard({ title, value, color }) {

    const colors = {
        green: "bg-green-100 text-green-700",
        red: "bg-red-100 text-red-700",
        blue: "bg-blue-100 text-blue-700",
        orange: "bg-orange-100 text-orange-700",
        purple: "bg-purple-100 text-purple-700",
        teal: "bg-teal-100 text-teal-700",
        slate: "bg-slate-100 text-slate-700",
    };

    return (
        <div className="rounded-2xl border bg-white shadow p-6">

            <div className="text-slate-500">
                {title}
            </div>

            <div
                className={`inline-block mt-4 rounded-xl px-4 py-2 text-3xl font-black ${colors[color]}`}
            >
                {value}
            </div>

        </div>
    );
}

function Panel({ title, subtitle, children }) {
    return (
        <div className="rounded-2xl bg-white border shadow p-6">

            <h2 className="text-2xl font-bold">
                {title}
            </h2>

            <p className="text-slate-500 mb-5">
                {subtitle}
            </p>

            <div className="space-y-3">
                {children}
            </div>

        </div>
    );
}

function Metric({ label, value }) {
    return (
        <div className="flex justify-between rounded-xl bg-slate-50 p-4">

            <span className="font-medium">
                {label}
            </span>

            <span className="font-black">
                {value}
            </span>

        </div>
    );
}

function Recommendation({ text }) {
    return (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">

            <div className="font-bold text-indigo-700">
                🤖 AI Recommendation
            </div>

            <div className="mt-2 text-slate-700">
                {text}
            </div>

        </div>
    );
}

function Status({ text }) {
    return (
        <div className="flex justify-between rounded-xl bg-green-50 border border-green-200 p-4">

            <span>{text}</span>

            <span className="font-bold text-green-700">
                ONLINE
            </span>

        </div>
    );
}